import { AcceptPage } from '@/cms/definitions/content-types';
import { CmsPolicy, DigitalAcceptCmsPage, Policy } from '@/types/types';
import { flattenProductFields } from './FieldService';
import { POLICIES, POLICY_FIELDS } from '@/types/enums';
import { formatDate, ukDateFormat } from './FormatService';

export const normalizeModelFromApi = (proposal: DigitalAcceptCmsPage, cmsModel: AcceptPage) => {
	for (const policy of proposal.policies) {
		flattenProductFields(policy);
		mapTypeAndTitle(policy, cmsModel);
		mapDescription(policy);
	}
};

export const mapTypeAndTitle = (policy: Policy, cmsModel: AcceptPage) => {
	const isAcceptPage = cmsModel.contentType === 'AcceptPage';
	const mappedProductType = getProductType(policy);

	if (isAcceptPage) {
		// accept page
		for (const policyRawBlock of cmsModel.adjustPoliciesBlock?.policyBlock?.items || []) {
			if (policyRawBlock.content.type.toLocaleLowerCase() === mappedProductType.toLocaleLowerCase()) {
				policyRawBlock.content.originalType = policy.type;
				policy.type = mappedProductType;
				policy.title = policyRawBlock.content.title;
				break;
			}
		}
	} else {
		// receipt page
		for (const policyBlock of cmsModel?.productNames || []) {
			if (policyBlock.key.toLocaleLowerCase() === mappedProductType.toLocaleLowerCase()) {
				policy.title = policyBlock.value;
				break;
			}
		}
	}

	mapAccidentTitle(policy);
};

const getProductType = (policy: Policy): string => {
	let policyType = policy.type;
	if (policy.type.startsWith(POLICIES.CONTENT)) {
		policyType = POLICIES.CONTENT;
	}
	if (policy.type.endsWith(POLICIES.MOPED)) {
		policyType = POLICIES.MOPED;
	}
	return policyType;
};

const mapAccidentTitle = (policy: Policy) => {
	if (policy.type === POLICIES.ACCIDENT) {
		switch (policy?.policy_object?.accident_insurance_type) {
			case 'ADULT':
				policy.title = 'Voksenulykke';
				break;
			case 'CHILD':
				policy.title = 'Børneulykke';
				break;
			case 'EXTENDED_REPLACEMENT':
				policy.title = 'Udvidet strakserstatning';
				break;
			case 'ASSAULT':
				policy.title = 'Overfald';
				break;
			default:
				break;
		}
	}
};

export const mapDescription = (policy) => {
	switch (policy.type) {
		case POLICIES.ACCIDENT:
			policy.description = policy.policy_object?.job_title ?? policy.policy_object?.name ?? policy.description;
			if (policy?.description?.trim() === policy?.title?.trim()) {
				policy.description = '';
			}
			break;
		case POLICIES.DOG:
			policy.description = policy.policy_object?.race ?? policy.description;
			break;
		case POLICIES.TRAVEL:
			if (policy.policy_object?.coverage_area === 'EUROPE') {
				policy.description = 'Dækker Europa';
			} else if (policy.policy_object?.coverage_area === 'WORLD') {
				policy.description = 'Dækker Verden';
			}
			break;
		case POLICIES.CAR:
		case POLICIES.TRAILER:
		case POLICIES.CARAVAN:
			policy.description = policy.policy_object?.brand ?? policy.description;
			break;
	}
};

export const handleProductHacks = (cmsPolicy: CmsPolicy) => {
	const policyType = cmsPolicy.type.toUpperCase();
	switch (policyType) {
		case POLICIES.CAR: {
			const mileageField = cmsPolicy.inputFields.find((field) => field.id === POLICY_FIELDS.mileage);
			const mileage = mileageField.value as string;
			if (!mileage || mileage?.trim() === '') {
				// if no mileage is set in offer, set mileageDate to today and hide the field.
				// mileage will be filled by user in the accept flow
				const mileageFieldDate = cmsPolicy.inputFields.find((field) => field.id === POLICY_FIELDS.mileage_date);
				mileageFieldDate.value = formatDate(new Date(), ukDateFormat);
				mileageFieldDate.hidden = true;
				mileageFieldDate.isValid = true;
			}

			break;
		}
		case POLICIES.HOLIDAY_HOUSE:
		case POLICIES.HOUSE: {
			const previousPolicyId = cmsPolicy.inputFields.find(
				(field) => field.id === POLICY_FIELDS.previous_policy_id
			);
			const previousCompanyId = cmsPolicy.inputFields.find(
				(field) => field.id === POLICY_FIELDS.previous_company_id
			);
			const terminationOption = cmsPolicy.inputFields.find(
				(field) => field.id === POLICY_FIELDS.termination_option
			);

			if (
				terminationOption?.value === 'CHANGE_OF_OWNERSHIP' &&
				previousPolicyId?.value &&
				previousCompanyId?.value
			) {
				// do not send to API if change of ownership and previous policy and company id is set.
				terminationOption.submitToApi = false;
				previousPolicyId.submitToApi = false;
				previousCompanyId.submitToApi = false;
			}
			break;
		}
	}
};
